import React from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  Button,
  useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AssetInformationStep from './AssetInformationStep';
import { AddAssetProvider, useAddAsset } from '../../../context/AddAssetContext';
import UploadStep from './UploadStep';
import UploadProgressList from './UploadProgressList';

interface AddAssetModalContentProps {
  onClose: () => void;
}

const AddAssetModalContent: React.FC<AddAssetModalContentProps> = ({ onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    activeStep,
    handleNext,
    handleBack,
    handleRunAsset,
    assetId,
    assetIdError,
    selectedConfigName,
    selectedConfiguration,
    noConfigsFound,
    steps,
    isUploading,
    allUploadsComplete,
    filesToUpload,
    isFinalizing
  } = useAddAsset();

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <AssetInformationStep />;
      case 1:
        return <UploadStep />;
      case 2:
        return <UploadProgressList />;
      default:
        return <Typography sx={{ mt: 2 }}>Unknown Step</Typography>;
    }
  };

  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: isMobile ? '100%' : '90%', // Full width on mobile
    height: isMobile ? '100%' : 'auto', // Full height on mobile
    maxWidth: '800px',
    bgcolor: 'background.paper',
    border: isMobile ? 'none' : `2px solid ${theme.palette.divider}`, // No border on mobile
    boxShadow: 24,
    p: isMobile ? 2 : 4, // Tighter padding on mobile
    borderRadius: isMobile ? 0 : '8px', // No radius on mobile
    display: 'flex', // Use flexbox for layout
    flexDirection: 'column',
    overflowY: 'auto' // Allow content to scroll if needed
  };

  const isNextDisabled = () => {
    if (activeStep === 0) {
      // Disable on first step if required info is missing or if uploads are happening
      return !assetId || !selectedConfigName || noConfigsFound || isUploading;
    }
    if (activeStep === 1) {
      return isUploading || filesToUpload.length === 0;
    }
    if (activeStep === 2) {
      return isUploading || !allUploadsComplete || isFinalizing;
    }
    return false; // Enable for last step
  };

  const canRunAsset = assetId && selectedConfiguration?.clientProcess?.id && !assetIdError

  return (
    <Box sx={style}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography id="add-asset-modal-title" variant="h6" component="h2">
          Add New Asset
        </Typography>
        <IconButton data-cy="add-asset-close-btn" aria-label="close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>


      {activeStep > 0 && assetId && (
        <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
          AssetId: {assetId}
        </Typography>
      )}

      {isMobile ? (
        <Typography variant="body1" sx={{ mt: 2, mb: 2, fontWeight: 500 }}>
          Step {activeStep + 1} of {steps.length}: {steps[activeStep]}
        </Typography>
      ) : (
        <Stepper
          activeStep={activeStep}
          sx={{
            mt: 3,
            mb: 2,
            '& .MuiStepIcon-root.Mui-active': {
              color: 'error.main',
            },
            '& .MuiStepIcon-root.Mui-completed': {
              color: 'error.main',
            },
          }}
        >
          {(steps || []).map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      <Box sx={{ flex: 1, overflowY: 'auto', py: 2 }}>
        {renderStepContent(activeStep)}
      </Box>

      <Box sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column-reverse' : 'row', // Stack vertically on mobile
        gap: 1,
        pt: 2,
        mt: 3
      }}>
        {isMobile ? (
          // --- MOBILE FOOTER ---
          <>
            <Button
              data-cy="add-asset-next-btn"
              fullWidth
              variant="contained"
              onClick={handleNext}
              disabled={isNextDisabled()}
              sx={{
                backgroundColor: (theme) => theme.palette.status.accent,
                color: '#fff',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.status.accent,
                  opacity: 0.9,
                },
                '&.Mui-disabled': {
                  opacity: 0.6,
                },
              }}
            >
              {isUploading ? 'Uploading...' : (isFinalizing ? 'Saving...' : (activeStep === steps.length - 1 ? 'Save' : 'Next'))}
            </Button>
            <Button data-cy="add-asset-run-btn" fullWidth variant="outlined" color="secondary" onClick={handleRunAsset} disabled={!canRunAsset}>
              Run Asset on Odroid
            </Button>
            <Button data-cy="add-asset-back-btn" fullWidth variant="outlined" color="inherit" disabled={activeStep === 0} onClick={handleBack}>
              Back
            </Button>
          </>
        ) : (
          // --- DESKTOP FOOTER ---
          <>
            <Button data-cy="add-asset-back-btn" variant="outlined" color="inherit" disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
              Back
            </Button>
            <Button data-cy="add-asset-run-btn" variant="outlined" color="inherit" onClick={handleRunAsset} disabled={!canRunAsset}>
              Run Asset on Odroid
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />
            <Button
              data-cy="add-asset-next-btn"
              variant="contained"
              onClick={handleNext}
              disabled={isNextDisabled()}
              sx={{
                backgroundColor: (theme) => theme.palette.status.accent,
                color: '#fff',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.status.accent,
                  opacity: 0.9,
                },
                '&.Mui-disabled': {
                  opacity: 0.6,
                },
              }}
            >
              {isUploading ? 'Uploading...' : (isFinalizing ? 'Saving...' : (activeStep === steps.length - 1 ? 'Save' : 'Next'))}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

interface AddAssetModalProps {
  open: boolean;
  onClose: () => void;
}

const AddAssetModal: React.FC<AddAssetModalProps> = ({ open, onClose }) => {
  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="add-asset-modal-title">
      <AddAssetProvider onClose={onClose}>
        <AddAssetModalContent onClose={onClose} />
      </AddAssetProvider>
    </Modal>
  );
};

export default AddAssetModal;
